import { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "../api";
import { PageHeader, Badge, dt } from "../components/common";
import { toast } from "sonner";
import { Search, Paperclip, Download, FileText, Image as ImgIcon } from "lucide-react";

function iconForMime(mime = "") {
  if (mime.startsWith("image/")) return ImgIcon;
  return FileText;
}

function fmtSize(n) {
  if (!n) return "-";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

async function downloadAnexo(articleId, anxId, nome) {
  try {
    const { data } = await api.get(`/kb/articles/${articleId}/anexos/${anxId}`);
    const b64 = data.content_b64 || "";
    const bin = atob(b64);
    const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
    const blob = new Blob([bytes], { type: data.mime || "application/octet-stream" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = data.nome || nome || "anexo"; a.click();
    URL.revokeObjectURL(url);
  } catch (e) {
    toast.error(formatApiErrorDetail(e.response?.data?.detail));
  }
}

export default function KBAttachments() {
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("");
  const [data, setData] = useState({ total: 0, categorias: [], items: [] });
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q.trim()) params.set("q", q.trim());
      if (cat) params.set("categoria", cat);
      const { data } = await api.get(`/kb/attachments?${params.toString()}`);
      setData(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setLoading(false); }
  };

  // Debounce da pesquisa
  useEffect(() => { const t = setTimeout(load, 220); return () => clearTimeout(t); }, [q, cat]); // eslint-disable-line

  const grupos = useMemo(() => {
    const g = {};
    for (const it of (data.items || [])) {
      const c = it.categoria || "geral";
      if (!g[c]) g[c] = {};
      const at = it.article_titulo || "(sem titulo)";
      if (!g[c][at]) g[c][at] = { article_id: it.article_id, items: [] };
      g[c][at].items.push(it);
    }
    return g;
  }, [data]);

  const cats = Object.keys(grupos).sort();

  return (
    <div data-testid="kb-attachments-page">
      <PageHeader
        overline="Base de Conhecimento"
        title="Central de Anexos"
      />
      <p className="text-sm text-muted2 max-w-3xl mb-4">
        Vista global de todos os anexos disponiveis nos artigos da Base de Conhecimento. Pesquisa por nome do ficheiro ou titulo do artigo, filtra por categoria e faz download rapido para usar em respostas a clientes.
      </p>
      <div className="flex flex-wrap gap-2 mb-5 items-center">
        <div className="relative flex-1 min-w-[240px] max-w-md">
          <Search size={15} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted2" />
          <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="anexos-search"
            placeholder="Pesquisar por nome do anexo ou titulo do artigo..."
            className="w-full pl-8 pr-3 py-2 border border-tint rounded-lg text-sm bg-white" />
        </div>
        <select value={cat} onChange={(e) => setCat(e.target.value)} data-testid="anexos-filter-cat"
          className="px-3 py-2 border border-tint rounded-lg text-sm bg-white">
          <option value="">{`Todas as categorias (${data.categorias.length})`}</option>
          {data.categorias.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <span className="text-xs text-muted2 ml-auto" data-testid="anexos-total">
          {loading ? "a carregar..." : `${data.total} anexo(s)`}
        </span>
      </div>

      {!loading && data.total === 0 && (
        <div className="text-center py-16 text-muted2 border-2 border-dashed border-tint rounded-xl">
          <Paperclip size={30} className="mx-auto mb-2 opacity-50" />
          <p className="text-sm">Sem anexos correspondentes.</p>
          <p className="text-xs mt-1">Adicione anexos aos artigos em Definicoes -&gt; Base de Conhecimento.</p>
        </div>
      )}

      {cats.map((c) => (
        <div key={c} className="mb-6" data-testid={`anexos-cat-${c}`}>
          <div className="flex items-center gap-2 mb-2 pb-2 border-b border-tint">
            <Badge value={c} />
            <span className="text-xs text-muted2">
              {Object.values(grupos[c]).reduce((s, g) => s + g.items.length, 0)} anexo(s)
              {" em "}{Object.keys(grupos[c]).length} artigo(s)
            </span>
          </div>
          <div className="space-y-3">
            {Object.entries(grupos[c]).map(([titulo, g]) => (
              <div key={g.article_id} className="rounded-lg border border-tint bg-white p-3" data-testid={`anexos-art-${g.article_id}`}>
                <div className="text-sm font-semibold text-navy mb-1.5">{titulo}</div>
                <div className="grid gap-1.5 md:grid-cols-2">
                  {g.items.map((it) => {
                    const Icon = iconForMime(it.mime);
                    return (
                      <div key={it.id} className="flex items-center gap-2 rounded bg-tint/60 px-2 py-1.5" data-testid={`anexo-row-${it.id}`}>
                        <Icon size={14} className="text-blue shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm text-navy truncate">{it.nome}</div>
                          <div className="text-[11px] text-muted2">{it.mime || "-"} - {fmtSize(it.tamanho)}{it.criado_em ? ` - ${dt(it.criado_em)}` : ""}</div>
                        </div>
                        <button type="button" onClick={() => downloadAnexo(g.article_id, it.id, it.nome)}
                          data-testid={`anexo-download-${it.id}`}
                          className="text-xs px-2 py-1 rounded bg-blue text-white hover:bg-blue/90 transition-colors inline-flex items-center gap-1 shrink-0">
                          <Download size={12} /> Descarregar
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
